import React, { useCallback, useEffect, useState } from 'react';
import { Eye, EyeOff, Copy, Check, RefreshCw, AlertTriangle, Banknote } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import { useAuth } from '../context/AuthContext';
import { adminApi, AdminWithdrawal } from '../api';

const statusStyle: Record<string, string> = {
  cooling_off: 'bg-gray-100 text-gray-600',
  ready: 'bg-amber-100 text-amber-800',
  processing: 'bg-blue-100 text-blue-800',
};

const AdminWithdrawals: React.FC = () => {
  const { user } = useAuth();
  const [rows, setRows] = useState<AdminWithdrawal[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<number | null>(null);
  // Revealed full bank details, keyed by withdrawal id (kept in memory only).
  const [revealed, setRevealed] = useState<Record<number, string>>({});
  const [copiedId, setCopiedId] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setRows(await adminApi.listWithdrawals());
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not load withdrawals');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (user?.is_admin) load();
  }, [user, load]);

  const reveal = async (id: number) => {
    if (revealed[id]) {
      setRevealed((r) => {
        const next = { ...r };
        delete next[id];
        return next;
      });
      return;
    }
    try {
      const { bank_account } = await adminApi.revealBankDetails(id);
      setRevealed((r) => ({ ...r, [id]: bank_account }));
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Could not reveal bank details');
    }
  };

  const copy = async (id: number, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedId(id);
      setTimeout(() => setCopiedId((c) => (c === id ? null : c)), 1500);
    } catch {
      /* clipboard blocked — ignore */
    }
  };

  const approve = async (w: AdminWithdrawal) => {
    if (!window.confirm(`Approve ${w.user_name}'s withdrawal of R${w.amount_zar.toFixed(2)}? This moves it to "processing".`)) return;
    setBusyId(w.id);
    try {
      await adminApi.approveWithdrawal(w.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Approve failed');
    } finally {
      setBusyId(null);
    }
  };

  const release = async (w: AdminWithdrawal) => {
    if (!window.confirm(`Skip the 3-day cooling-off for ${w.user_name}'s R${w.amount_zar.toFixed(2)} withdrawal and make it ready to pay now?`)) return;
    setBusyId(w.id);
    try {
      await adminApi.releaseWithdrawal(w.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Release failed');
    } finally {
      setBusyId(null);
    }
  };

  const complete = async (w: AdminWithdrawal) => {
    if (!window.confirm(
      `Mark R${w.amount_zar.toFixed(2)} to ${w.user_name} as PAID?\n\n` +
      `Only do this AFTER you have made the bank transfer. This is final and cannot be undone.`
    )) return;
    setBusyId(w.id);
    try {
      await adminApi.completeWithdrawal(w.id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail || 'Complete failed');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <AdminLayout>
      <div>
        <div className="flex items-center justify-end mb-3">
          <button
            onClick={load}
            className="flex items-center gap-1.5 text-sm text-green-300/80 hover:text-white"
          >
            <RefreshCw className="h-4 w-4" /> Refresh
          </button>
        </div>

        <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3 mb-4 flex gap-2 text-sm text-yellow-100/90">
          <AlertTriangle className="h-4 w-4 text-yellow-400 shrink-0 mt-0.5" />
          <span>
            Every action here is logged. Make the bank transfer first, then mark
            the payout as paid. Revealing bank details is recorded against your account.
          </span>
        </div>

        {error && (
          <div className="bg-red-500/15 text-red-200 border border-red-500/30 p-3 rounded-xl text-sm mb-4">{error}</div>
        )}

        {loading ? (
          <div className="text-green-200/80 py-10 text-center">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="bg-black/30 border border-green-800/50 rounded-2xl p-8 text-center text-green-200/80">
            <Banknote className="h-8 w-8 mx-auto mb-2 text-green-500/70" />
            No withdrawals to process right now.
          </div>
        ) : (
          <div className="space-y-3">
            {rows.map((w) => (
              <div key={w.id} className="bg-white rounded-2xl p-4 shadow-sm">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-semibold text-gray-900 truncate">{w.user_name}</div>
                    <div className="text-xs text-gray-500 truncate">{w.user_email}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-lg font-extrabold text-gray-900">R{w.amount_zar.toFixed(2)}</div>
                    <span className={`text-xs px-2 py-0.5 rounded-full whitespace-nowrap ${statusStyle[w.status] || 'bg-gray-100 text-gray-700'}`}>
                      {w.status === 'ready' ? 'Ready' : w.status === 'processing' ? 'Processing' : w.status === 'cooling_off' ? 'Cooling off' : w.status}
                    </span>
                  </div>
                </div>

                <div className="mt-3 bg-gray-50 rounded-xl p-3 text-sm">
                  <div className="flex items-center justify-between gap-2">
                    <span className="text-gray-500">Bank details</span>
                    <div className="flex items-center gap-2">
                      {revealed[w.id] && (
                        <button
                          onClick={() => copy(w.id, revealed[w.id])}
                          className="flex items-center gap-1 text-emerald-700 hover:text-emerald-800 text-xs"
                        >
                          {copiedId === w.id ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                          {copiedId === w.id ? 'Copied' : 'Copy'}
                        </button>
                      )}
                      <button
                        onClick={() => reveal(w.id)}
                        className="flex items-center gap-1 text-gray-600 hover:text-gray-900 text-xs"
                      >
                        {revealed[w.id] ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
                        {revealed[w.id] ? 'Hide' : 'Reveal'}
                      </button>
                    </div>
                  </div>
                  <div className="mt-1 font-mono text-gray-800 break-all whitespace-pre-line">
                    {revealed[w.id] || w.bank_account_masked}
                  </div>
                  {w.notes && <div className="mt-2 text-xs text-gray-500">Note: {w.notes}</div>}
                  <div className="mt-1 text-xs text-gray-400">
                    Requested {new Date(w.requested_at).toLocaleDateString()}
                  </div>
                </div>

                <div className="mt-3 flex gap-2">
                  {w.status === 'cooling_off' && (
                    <button
                      onClick={() => release(w)}
                      disabled={busyId === w.id}
                      className="flex-1 bg-amber-500 text-white py-2.5 rounded-xl font-semibold hover:bg-amber-600 transition disabled:opacity-50"
                    >
                      {busyId === w.id ? 'Working…' : 'Release now (skip cooling-off)'}
                    </button>
                  )}
                  {w.status === 'ready' && (
                    <button
                      onClick={() => approve(w)}
                      disabled={busyId === w.id}
                      className="flex-1 bg-emerald-600 text-white py-2.5 rounded-xl font-semibold hover:bg-emerald-700 transition disabled:opacity-50"
                    >
                      {busyId === w.id ? 'Working…' : 'Approve'}
                    </button>
                  )}
                  {w.status === 'processing' && (
                    <button
                      onClick={() => complete(w)}
                      disabled={busyId === w.id}
                      className="flex-1 bg-green-700 text-white py-2.5 rounded-xl font-semibold hover:bg-green-800 transition disabled:opacity-50"
                    >
                      {busyId === w.id ? 'Working…' : 'Mark as paid'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminWithdrawals;
